import { getAhakiCounterpartCheck } from '@/services/main/consent';
import type { AhakiCounterpart } from '@/services/main/consent';
import type { ConsentFormData } from '@/schemas/consentSchema';

type AhakiFlag = 'is_ahaki_both_treatment' | 'is_not_transport_cost';

export type AhakiCrossCheckContext = {
  serviceType: 'r2' | 'a2';
  siteType: string;
  clinicNumber: string;
  patientId: string;
  /** OK/Cancel — resolves true (proceed) or false (cancel). */
  confirmFn: (message: string) => Promise<boolean>;
  /** Warning OK-only — always blocks after dismissed. */
  alertFn: (message: string) => Promise<void>;
  /** Called when a flag is auto-set (Group A-2/A-3) so the form UI stays in sync. */
  onAutoSet?: (field: AhakiFlag, value: boolean) => void;
};

/**
 * Submit-time あはき両方施術 cross-check (Groups A / B / C).
 * Returns true = proceed with save, false = block.
 */
export async function runAhakiCrossCheck(
  data: ConsentFormData,
  ctx: AhakiCrossCheckContext
): Promise<boolean> {
  const { serviceType, siteType, clinicNumber, patientId, confirmFn, onAutoSet } = ctx;

  if (data.walking_condition_id !== 1) return true;

  const from = data.treatment_term_from;
  const to = data.treatment_term_to;
  if (!from || !to) return true;

  let response;
  try {
    response = await getAhakiCounterpartCheck(
      siteType, clinicNumber, patientId, serviceType.toUpperCase(), from, to
    );
  } catch {
    return true;
  }

  const counterpartList = response?.data?.counterparts?.length
    ? response.data.counterparts
    : response?.data?.counterpart
      ? [response.data.counterpart]
      : [];
  const walkingCounterparts = counterpartList.filter(cp => cp.walking_condition_id === 1);
  if (!response?.data?.has_counterpart || walkingCounterparts.length === 0) return true;

  const isAhakiBoth = !!data.is_ahaki_both_treatment;
  const isNotTransport = !!data.is_not_transport_cost;
  const counterpartType = serviceType === 'r2' ? 'A2' : 'R2';

  const autoUpdateCounterparts = async (
    flag: AhakiFlag,
    targets: AhakiCounterpart[]
  ): Promise<boolean> => {
    if (targets.length === 0) return true;

    const flagLabel = flag === 'is_ahaki_both_treatment' ? '『あはき両方施術』' : '『通所』';
    if (!await confirmFn(`${counterpartType}の同意書を${flagLabel}に更新します。よろしいですか？`)) {
      return false;
    }

    // HIT-7357: no separate PATCH any more — the counterpart update now runs inside the same
    // backend transaction as this save, so a failed save can no longer leave it half-applied.
    data.ahaki_sync_counterparts = true;
    return true;
  };

  // ── Group A: current has neither flag ──────────────────────────────────────
  // HIT-3110 spec, restored after HIT-5703/HIT-7357 dropped it: when every walking
  // counterpart unambiguously already leans one way, auto-set the CURRENT record to
  // the other flag (no counterpart PATCH — the current save already carries it).
  // Only warn (HIT-7357: OK saves with both boxes left off) when counterparts give no
  // clear direction — none set, or they disagree with each other.
  if (!isAhakiBoth && !isNotTransport) {
    const cpHasAhaki = walkingCounterparts.some(
      cp => cp.is_ahaki_both_treatment && !cp.is_not_transport_cost
    );
    const cpHasTransport = walkingCounterparts.some(
      cp => cp.is_not_transport_cost && !cp.is_ahaki_both_treatment
    );
    if (cpHasAhaki && !cpHasTransport) {
      data.is_not_transport_cost = true; // A-2
      onAutoSet?.('is_not_transport_cost', true);
      return true;
    }
    if (cpHasTransport && !cpHasAhaki) {
      data.is_ahaki_both_treatment = true; // A-3
      onAutoSet?.('is_ahaki_both_treatment', true);
      return true;
    }
    // A-1: no clear direction — let user decide whether to register without either flag.
    return confirmFn(
      '鍼灸、マッサージの同時施術の可能性があります。' +
        '「あはき両方施術」または「通所」にチェックせずにこのまま登録してよろしいですか？'
    );
  }

  // ── Group B: current has is_ahaki_both_treatment ───────────────────────────
  if (isAhakiBoth && !isNotTransport) {
    const targets = walkingCounterparts.filter(counterpart => !counterpart.is_not_transport_cost);
    return autoUpdateCounterparts('is_not_transport_cost', targets); // B-1 / B-2
  }

  // ── Group C: current has is_not_transport_cost ─────────────────────────────
  if (!isAhakiBoth && isNotTransport) {
    const targets = walkingCounterparts.filter(counterpart => !counterpart.is_ahaki_both_treatment);
    return autoUpdateCounterparts('is_ahaki_both_treatment', targets); // C-1 / C-2
  }

  return true;
}
